import React, { useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Popper, PopperProps, TextField, Typography } from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { compareVersions, linesBetween, versionLine } from "./changelog";
import { MAX_RANGE_LINES } from "./versionDetails";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fields: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: theme.spacing(2),
      marginTop: theme.spacing(2)
    },
    help: {
      fontSize: 13.5,
      color: theme.palette.text.secondary
    },
    error: {
      fontSize: 13,
      color: theme.palette.error.main,
      marginTop: theme.spacing(1.5)
    },
    listbox: {
      maxHeight: 260
    }
  })
);

/** Keeps the version list below the field, even when the dialog sits low on the screen */
const BottomPopper: React.FC<PopperProps> = (props) => (
  <Popper {...props} placement="bottom-start" modifiers={{ flip: { enabled: false } }} />
);

interface VersionRangeDialogProps {
  open: boolean;
  /** Every version in the CHANGELOG, newest first */
  versions: string[];
  /** Version selected in Novidades, used as the initial interval */
  initial: string;
  onClose: () => void;
  /** Called with the interval in ascending order */
  onConfirm: (from: string, to: string) => void;
}

/** Picks an interval of versions, at most MAX_RANGE_LINES version lines wide, for the summary of versions */
const VersionRangeDialog: React.FC<VersionRangeDialogProps> = ({ open, versions, initial, onClose, onConfirm }) => {
  const classes = useStyles();
  const [from, setFrom] = useState(initial);
  const [to, setTo] = useState(initial);

  useEffect(() => {
    if (open) {
      setFrom(initial);
      setTo(initial);
    }
  }, [open, initial]);

  const [low, high] = compareVersions(from, to) <= 0 ? [from, to] : [to, from];
  const lines = linesBetween(versions, low, high);
  const tooWide = lines.length > MAX_RANGE_LINES;
  // A text field that filters the versions as you type, with the list grouped by version line
  const field = (label: string, value: string, onChange: (version: string) => void) => (
    <Autocomplete
      options={versions}
      value={value}
      onChange={(_, version) => version && onChange(version)}
      groupBy={(version) => `Linha ${versionLine(version)}`}
      disableClearable
      openOnFocus
      autoHighlight
      noOptionsText="Nenhuma versão"
      PopperComponent={BottomPopper}
      classes={{ listbox: classes.listbox }}
      renderInput={(params) => <TextField {...params} label={label} placeholder="Filtrar versão" variant="outlined" size="small" />}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="version-range-title">
      <DialogTitle id="version-range-title">Resumo de versões</DialogTitle>
      <DialogContent>
        <Typography className={classes.help}>
          {`Escolha até ${MAX_RANGE_LINES} linhas de versão, como 1.106.0 → 1.107.13. O resumo junta destaques, melhorias e correções.`}
        </Typography>
        <div className={classes.fields}>
          {field("De", from, setFrom)}
          {field("Até", to, setTo)}
        </div>
        {tooWide && (
          <Typography className={classes.error} role="alert">
            {`O intervalo passa por ${lines.length} linhas (${lines.join(", ")}). Escolha até ${MAX_RANGE_LINES}.`}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="primary" variant="contained" disabled={tooWide} onClick={() => onConfirm(low, high)}>Abrir resumo</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VersionRangeDialog;
